import React from "react";
import { Card, CircularProgress } from "@material-ui/core";
import { View } from "react-native-web";
import { PrimaryText } from "components/Common/PrimaryText";
import InfoTooltip from "components/RetentionDashboard/InfoTooltip";
import { useDisplay } from "context/DisplayContext";
import { useForeverX } from "context/ForeverXContext";
import { hearAboutUsOptions } from "constants/data";

const UNKNOWN_ID = "unknown";
const UNKNOWN_NAME = "Unknown / no answer";
const BAR_COLOR = "#1976d2";
const UNKNOWN_BAR_COLOR = "#9e9e9e";

const ReferralSourcesChart = ({ feedBackByType, loading }) => {
  const { isMobile } = useDisplay();
  const { companyData } = useForeverX();

  const answeredTotal = feedBackByType.reduce((sum, item) => sum + (item?.count || 0), 0);
  const currentUsersCount = companyData?.currentUsersCount;
  const unknownCount =
    typeof currentUsersCount === "number" && currentUsersCount > answeredTotal
      ? currentUsersCount - answeredTotal
      : 0;

  const answeredRows = feedBackByType.map((item) => {
    const option = hearAboutUsOptions.find((x) => x.id === item?.hearAboutUs);
    return {
      id: item?.hearAboutUs,
      name: option?.name || item?.hearAboutUs,
      count: item?.count || 0,
      isUnknown: false,
    };
  });

  const rows =
    unknownCount > 0
      ? [
          ...answeredRows,
          { id: UNKNOWN_ID, name: UNKNOWN_NAME, count: unknownCount, isUnknown: true },
        ]
      : answeredRows;

  const sortedRows = rows.sort((a, b) => b.count - a.count);
  const maxCount = sortedRows.reduce((max, row) => Math.max(max, row.count), 0) || 1;

  if (loading) {
    return (
      <Card style={{ padding: isMobile ? 12 : 20, textAlign: "center" }}>
        <CircularProgress />
        <PrimaryText style={{ marginTop: 12 }}>Loading referral sources...</PrimaryText>
      </Card>
    );
  }

  if (sortedRows.length === 0) {
    return (
      <Card style={{ padding: isMobile ? 12 : 20, textAlign: "center" }}>
        <PrimaryText style={{ color: "#666" }}>No referral source data available</PrimaryText>
      </Card>
    );
  }

  return (
    <Card style={{ padding: isMobile ? 12 : 20 }}>
      <View style={{ flexDirection: "row", alignItems: "center", marginBottom: 20 }}>
        <PrimaryText fontSize={isMobile ? 16 : 20} fontWeight={700}>
          Referral Sources
        </PrimaryText>
        <InfoTooltip
          title="Referral Sources"
          content="Where users said they heard about ForeverX during onboarding. Unknown covers users who never answered."
        />
      </View>

      <View>
        {sortedRows.map((row, index) => {
          const barWidth = maxCount > 0 ? (row.count / maxCount) * 100 : 0;
          const isLast = index === sortedRows.length - 1;
          return (
            <View
              key={row.id}
              style={{
                flexDirection: isMobile ? "column" : "row",
                alignItems: isMobile ? "flex-start" : "center",
                marginBottom: isLast ? 0 : isMobile ? 10 : 12,
              }}
            >
              <View style={{ width: isMobile ? "100%" : 180, marginBottom: isMobile ? 4 : 0 }}>
                <PrimaryText fontSize={isMobile ? 12 : 14} fontWeight={600} numberOfLines={1}>
                  {row.name}
                </PrimaryText>
              </View>
              <View style={{ flex: 1, flexDirection: "row", alignItems: "center", width: isMobile ? "100%" : "auto" }}>
                <View
                  style={{
                    height: isMobile ? 20 : 24,
                    width: `${barWidth}%`,
                    backgroundColor: row.isUnknown ? UNKNOWN_BAR_COLOR : BAR_COLOR,
                    borderRadius: 4,
                    justifyContent: "center",
                    minWidth: row.count > 0 ? 4 : 0,
                  }}
                />
                <View style={{ width: 8 }} />
                <PrimaryText fontSize={isMobile ? 12 : 14} fontWeight={700}>
                  {row.count.toLocaleString()}
                </PrimaryText>
              </View>
            </View>
          );
        })}
      </View>
    </Card>
  );
};

export default ReferralSourcesChart;
